import { env } from '@/lib/env'
import { coerceGapAudit, type GapAudit } from '../../workers/audit-format'

/**
 * THE DOCUMENTATION AUDIT CLIENT (Build 10c).
 *
 * Talks to the owner-only GET /api/admin/audit (workers/api.ts). The shape and
 * every word the Report page shows come from workers/audit-format.ts, the
 * dependency-free module the engine uses too, so this file carries no wording
 * of its own. React-free, like src/lib/renewals.ts.
 *
 * NEVER TRUST THE WIRE: the response is read through coerceGapAudit.
 */

export type { GapAudit }

export class AuditError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'AuditError'
  }
}

/** The report for the days chosen. Either day may be left out: the engine
 *  then reports the fortnight ending today. Days only — never a tenant. */
export async function fetchAudit(
  token: string | null,
  days: { from?: string; to?: string } = {},
): Promise<GapAudit> {
  if (!token) throw new AuditError('Your sign-in has expired. Please sign in again.', 401)
  const query = new URLSearchParams()
  if (days.from) query.set('from', days.from)
  if (days.to) query.set('to', days.to)
  const qs = query.toString()
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}/api/admin/audit${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch {
    throw new AuditError('No connection. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new AuditError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: unknown } | null)?.message
    throw new AuditError(typeof message === 'string' ? message : 'Could not build the report.', res.status)
  }
  return coerceGapAudit(data)
}
